import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { SG_GET_ACTION_UPDATES } from 'constants/actions';
import { Link } from 'react-router-dom';
import PageIntro from '../components/PageIntro';

export default function UpdatesIndex() {
  const dispatch = useDispatch();

  // Avoid creating a new [] every render; use shallowEqual to prevent loops
  const updates = useSelector((s) => s.action_updates?.updates, shallowEqual);

  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fetch on page change
  useEffect(() => {
    const query = new URLSearchParams({ page: pageNumber }).toString();
    setLoading(true);
    dispatch({ type: SG_GET_ACTION_UPDATES, payload: query });
  }, [dispatch, pageNumber]);

  // Clear loading when count or results length changes (stable primitives)
  useEffect(() => {
    const count = updates?.count;
    const len = Array.isArray(updates?.results)
      ? updates.results.length
      : (Array.isArray(updates) ? updates.length : undefined);
    if (count !== undefined || len !== undefined) {
      setLoading(false);
    }
  }, [updates?.count, updates?.results?.length]);

  const list = Array.isArray(updates?.results)
    ? updates.results
    : (Array.isArray(updates) ? updates : []);

  const count = updates?.count ?? list.length;
  const page_size = updates?.page_size ?? (list.length || 10);
  const totalPages = page_size ? Math.max(1, Math.ceil(count / page_size)) : 1;

  function prev() {
    if (pageNumber > 1 && !loading) setPageNumber((p) => p - 1);
  }

  function next() {
    if (pageNumber < totalPages && !loading) setPageNumber((p) => p + 1);
  }

  return (
    <div>
      <PageIntro
        title="Action Updates"
        description="Action updates record progress and notes managers submit during check-ins. They help track progress over time and surface at-risk work items."
        bullets={[
          'Where data comes from: manager check-ins and manual admin updates.',
          'What to expect: update summaries, timestamps, and associated action IDs.',
          'Next steps: review updates to update status and follow up where required.'
        ]}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <div>Showing {list.length} of {count} updates</div>
        <div>
          <button onClick={prev} disabled={loading || pageNumber <= 1} style={{ marginRight: 8 }}>Prev</button>
          <span>Page {pageNumber} / {totalPages}</span>
          <button onClick={next} disabled={loading || pageNumber >= totalPages} style={{ marginLeft: 8 }}>Next</button>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
        <thead>
          <tr>
            <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>Title</th>
            <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>Org</th>
            <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>Type</th>
            <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>Created</th>
            <th style={{ borderBottom: '1px solid #ddd', padding: 8 }}>View</th>
          </tr>
        </thead>
        <tbody>
          {list.length ? (
            list.map((u) => (
              <tr key={u.id}>
                <td style={{ padding: 8 }}>{u.title || u.name || '-'}</td>
                <td style={{ padding: 8 }}>{u.organization?.name || '-'}</td>
                <td style={{ padding: 8 }}>{u.update_type || u.type || '-'}</td>
                <td style={{ padding: 8 }}>{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</td>
                <td style={{ padding: 8 }}>
                  {u.id && u.id !== 'undefined' ? (
                    <Link to={`details/${u.id}`}>View</Link>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>No id</span>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan={5}>{loading ? 'Loading...' : 'No updates'}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}